import { Component, OnDestroy, ViewChild, Input, Output, EventEmitter } from '@angular/core'
import { FormBuilder, FormArray } from '@angular/forms'
import { Subscription, Observable } from 'rxjs'
import { finalize } from 'rxjs/operators'
import { UiService } from 'src/app/services/ui.service'
import { subscriptionActive } from 'src/utils/rxjs-utils'
import { handleArpioError } from 'src/utils/handle-error'
import { AccountService } from '../../services/account.service'
import { ModalDialogComponent } from 'src/app/shared/modal-dialog/modal-dialog.component'
import { RoleManagementItem } from '../role-management/role-management.component'
import { ApiService } from 'src/app/services/api.service'
import { AccessLevelService } from 'src/app/services/access-level.service'
import { FormValidationService } from 'src/app/services/form-validation.service'
import { BaseRoleModal, ApplicationItem, PolicyChild } from '../shared/base-role-modal'
import { ModalService, ModalController } from '../../services/modal.service'

@Component({
  selector: 'app-edit-role-modal',
  templateUrl: './edit-role-modal.component.html',
  standalone: false,
})
export class EditRoleModalComponent extends BaseRoleModal implements OnDestroy, ModalController {
  @ViewChild('editRoleModal') modal: ModalDialogComponent
  @ViewChild('deleteRoleDialog', { static: true }) deleteRoleDialog: ModalDialogComponent
  @Input() role: RoleManagementItem | null = null
  @Output() roleUpdated = new EventEmitter<RoleManagementItem>()

  // Delete role dialog
  roleToDelete: RoleManagementItem | null = null

  constructor(
    protected formBuilder: FormBuilder,
    protected accountService: AccountService,
    protected uiService: UiService,
    protected apiService: ApiService,
    protected accessLevelService: AccessLevelService,
    protected formValidationService: FormValidationService,
    private modalService: ModalService
  ) {
    super(formBuilder, accountService, apiService, uiService, accessLevelService, formValidationService)
    // Register with modal service
    this.modalService.register(this, this)
  }

  ngOnDestroy() {
    this.cleanup()
    // Unregister from modal service
    this.modalService.unregister(this)
  }

  // Abstract method implementations
  getSaveButtonText(): string {
    return this.saving ? 'Saving...' : 'Save Changes'
  }

  performSave(rolePayload: any): Observable<any> {
    return this.apiService.updateRole(this.accountService.accountId, this.role.id, rolePayload as any)
  }

  onSaveSuccess(result: any): void {
    // Emit the updated role data, or the current role if no updated data is returned
    const updatedRole = result || this.role!
    this.roleUpdated.emit(updatedRole)
    this.close()
  }

  open(role: RoleManagementItem) {
    this.role = role
    this.modal.open()
    this.populateForm()
    // Load applications first, then load role policies to avoid race condition
    this.loadAvailableApplications()
  }

  close() {
    this.modal.close()
    this.resetForm()
  }

  isModalOpen(): boolean {
    return this.modal && this.modal.isOpen
  }

  isSystemRole(): boolean {
    return this.role?.isSystemRole || false
  }

  // Override save logic for system roles
  onSave() {
    if (this.isSystemRole()) {
      // For system roles, only save external ID and name, not policies
      this.saving = true
      const formData = this.roleForm.value

      const rolePayload = {
        name: formData.name, // Send the current name (readonly but required)
        externalId: formData.externalId || null,
        policies: [], // System roles don't have editable policies
      }

      this.saveSubscription = this.performSave(rolePayload)
        .pipe(
          finalize(() => {
            this.saving = false
            this.saveSubscription = null
          })
        )
        .subscribe(
          (result) => {
            this.onSaveSuccess(result)
          },
          (error) => {
            // Use standard error handling for all errors
            handleArpioError(window.location.href, this.apiService, this.uiService)(error)
          }
        )
    } else {
      // For regular roles, use the base class save logic which includes policies
      super.onSave()
    }
  }

  private populateForm() {
    if (this.role) {
      // Use existing role data - no need to reload from API
      this.roleForm.patchValue({
        name: this.role.name,
        externalId: this.role.externalId || '',
      })
    }
  }

  private loadRolePoliciesAndApplications() {
    if (this.role) {
      const policyChildrenArray = this.roleForm.get('policyChildren') as FormArray

      // Clear existing policy children
      while (policyChildrenArray.length) {
        policyChildrenArray.removeAt(0)
      }

      // Use existing policy data from the role instead of making an API call
      if (this.role.policies && this.role.policies.length > 0) {
        // Load policies from existing role data
        this.role.policies.forEach((policy, index) => {
          // For account-level permissions, applications should be empty since they don't apply to specific apps
          const selectedApps = this.convertAppIdsToApplicationItems(policy.appIds || [])
          const allApps = policy.allApps || false

          const policyName = `Policy ${index + 1}`

          this.addPolicyChildWithData(policyName, policy.accessLevel, allApps, selectedApps)
        })
      } else {
        // Fallback: create one policy based on the role's information
        let selectedApps = []
        let allApps = false
        let primaryAccessLevel = 'app_read' // default

        // Try to determine access level from available information
        if (this.role.applications.includes('All Applications')) {
          allApps = true
        } else {
          selectedApps = this.convertAppNamesToApplicationItems(this.role.applications)
        }

        this.addPolicyChildWithData('Primary Policy', primaryAccessLevel, allApps, selectedApps)
      }

      // Set policy accordion states: open if only one policy, closed if multiple
      const hasSinglePolicy = policyChildrenArray.length === 1

      this.policyCollapsedStates = []
      for (let i = 0; i < policyChildrenArray.length; i++) {
        this.policyCollapsedStates.push(!hasSinglePolicy) // Open (false) if single policy, closed (true) if multiple
      }

      // Add delay to ensure applications are fully loaded and DOM is updated
      setTimeout(() => {
        // Force refresh of policy displays to ensure applications are properly mapped
        this.refreshPolicyDisplays()
      }, 100)
    }
  }

  // Helper method to convert app IDs to ApplicationItem objects
  private convertAppIdsToApplicationItems(appIds: string[]): any[] {
    const result = appIds
      .map((appId) => {
        let app = this.availableApplications.find((a) => a.appId === appId)

        const mappedApp = app || { id: appId, name: appId, type: '', selected: false }
        return mappedApp
      })
      .filter((app) => app) // Remove any null/undefined entries

    return result
  }

  // Helper method to convert app names to ApplicationItem objects
  private convertAppNamesToApplicationItems(appNames: string[]): any[] {
    if (appNames.includes('All Applications')) {
      return []
    }
    return appNames.map((name) => {
      const app = this.availableApplications.find((a) => a.name === name)
      return app || { id: name, name: name, type: '', selected: false }
    })
  }

  protected loadAvailableApplications() {
    this.apiService.listApplications(this.accountService.accountId).subscribe(
      (applications) => {
        this.availableApplications = applications.map((app) => ({
          id: app.appId,
          name: app.name,
          type: app.type,
          selected: false,
          // Store both appId and name for better matching
          appId: app.appId,
        }))
        // Load role policies after applications are loaded to ensure proper mapping
        this.loadRolePoliciesAndApplications()
      },
      (error) => {
        console.error('Failed to load applications:', error)
        // Keep empty array as fallback
        this.availableApplications = []
        // Still try to load role policies even if applications failed
        this.loadRolePoliciesAndApplications()
      }
    )
  }

  private resetForm() {
    this.roleForm.reset()
    this.resetSelections()
    this.saving = false
    this.cleanup()
  }

  deleteRole() {
    if (!this.role) return

    this.roleToDelete = this.role
    this.deleteRoleDialog.open()
  }

  confirmDeleteRole() {
    if (!this.roleToDelete) return

    this.apiService.deleteRole(this.accountService.accountId, this.roleToDelete.id).subscribe(
      () => {
        this.deleteRoleDialog.close()
        this.roleUpdated.emit(this.roleToDelete!) // Emit event to refresh parent component
        this.close()
      },
      handleArpioError(window.location.href, this.apiService, this.uiService)
    )
  }

  cancelDeleteRole() {
    this.roleToDelete = null
    this.deleteRoleDialog.close()
  }
}
